import {
  ChangeDetectionStrategy,
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  Input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { tuiPure, TuiStringHandler, TuiTime } from '@taiga-ui/cdk';
import { DateTime, Duration } from 'luxon';

import { DateChangeService } from '@common/services/date-change.service';
import { transformLuxonDateToTuiDate } from '@common/utils/date-form-transform';
import { dateTimeWithUserTimezone } from '@common/utils/format-date';

export enum AvailableDateTimePeriod {
  Today = 'today',
  Recently = 'recently',
  Exact = 'exact',
}

export enum AvailableRecentlyDateTimePeriodTypes {
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
}

// export type DateTimePeriodParams = {
//   periodDateStart: string | null;
//   periodDateEnd: string | null;
//   periodValue: AvailableDateTimePeriod;
//   recentlyPeriod: number;
//   recentlyPeriodType: AvailableRecentlyDateTimePeriodTypes;
//   currentPeriod: boolean;
// };

export interface DateTimePeriodParamsForm {
  periodDateStart: FormControl<any>;
  periodDateEnd: FormControl<any>;
  periodValue: FormControl<AvailableDateTimePeriod | null>;
  recentlyPeriod: FormControl<number | null>;
  recentlyPeriodType: FormControl<AvailableRecentlyDateTimePeriodTypes | null>;
  currentPeriod: FormControl<boolean | null>;
}

export type DateTimePeriodParams = {
  [P in keyof DateTimePeriodParamsForm]: DateTimePeriodParamsForm[P]['value'];
};

export const availableDateTimePeriod: {
  value: AvailableDateTimePeriod;
  title: string;
}[] = [
  { value: AvailableDateTimePeriod.Today, title: 'Today' },
  { value: AvailableDateTimePeriod.Recently, title: 'Recently' },
  { value: AvailableDateTimePeriod.Exact, title: 'Exact interval' },
];

export const availableRecentlyDateTimePeriodTypes: {
  value: AvailableRecentlyDateTimePeriodTypes;
  title: string;
}[] = [
  { value: AvailableRecentlyDateTimePeriodTypes.Days, title: 'Days' },
  { value: AvailableRecentlyDateTimePeriodTypes.Weeks, title: 'Weeks' },
  { value: AvailableRecentlyDateTimePeriodTypes.Months, title: 'Months' },
];

@Component({
  selector: 'app-date-time-period',
  templateUrl: './date-time-period.component.html',
  styleUrls: ['./date-time-period.component.less'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DateTimePeriodComponent implements AfterViewInit {
  private readonly translate = inject(TranslateService);
  private readonly dateChangeService = inject(DateChangeService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly AvailableDateTimePeriod = AvailableDateTimePeriod;
  availablePeriods = availableDateTimePeriod;
  availableRecentlyPeriodTypes = availableRecentlyDateTimePeriodTypes;

  @Input() formGroup: FormGroup<DateTimePeriodParamsForm> = new FormGroup<DateTimePeriodParamsForm>(
    {
      periodDateStart: new FormControl(null, [Validators.required]),
      periodDateEnd: new FormControl(null, [Validators.required]),
      periodValue: new FormControl(AvailableDateTimePeriod.Today),
      recentlyPeriod: new FormControl(1),
      recentlyPeriodType: new FormControl<AvailableRecentlyDateTimePeriodTypes>(
        AvailableRecentlyDateTimePeriodTypes.Days,
      ),
      currentPeriod: new FormControl(false),
    },
  );

  @Input() disableDisplayChangePeriodValue = false;

  dateNow: DateTime = dateTimeWithUserTimezone();

  currentDatetime: any = transformLuxonDateToTuiDate(this.dateNow.endOf('day'), {
    withTime: true,
  });

  @tuiPure
  stringifyValue(
    items: {
      value: string | number;
      title: string;
    }[],
  ): TuiStringHandler<string | number> {
    const map: any = new Map(
      items.map(({ value, title }) => [value, title] as [string | number, string]),
    );

    return (value: string | number) => this.translate.instant(map.get(value) ?? '');
  }

  ngAfterViewInit() {
    const controls = this.formGroup.controls;

    if (!controls.periodDateStart.value) {
      controls.periodDateStart.setValue(
        transformLuxonDateToTuiDate(this.dateNow.startOf('day'), {}),
      );
    }

    if (!controls.periodDateEnd.value) {
      controls.periodDateEnd.setValue(transformLuxonDateToTuiDate(this.dateNow.endOf('day'), {}));
    }

    controls.periodDateStart.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const startDay = value?.[0];
        const endDay = controls.periodDateEnd.value?.[0];

        if (!startDay || !endDay) return;

        if (new Date(startDay.toUtcNativeDate()) > new Date(endDay.toUtcNativeDate())) {
          controls.periodDateEnd.setValue([startDay, new TuiTime(23, 59, 59, 999)]);
        }
      });

    controls.periodValue.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: any) => this.onChangePeriodValue(value));
    controls.currentPeriod.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setRecentlyDates());
    controls.recentlyPeriodType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setRecentlyDates());
    controls.recentlyPeriod.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.setRecentlyDates());

    this.dateChangeService
      .onDateChange()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newDate) => {
        this.currentDatetime = transformLuxonDateToTuiDate(this.dateNow.endOf('day'), {
          withTime: true,
        });

        const periodValue = this.formGroup.controls.periodValue.value;

        if (
          (periodValue === AvailableDateTimePeriod.Today ||
            periodValue === AvailableDateTimePeriod.Recently) &&
          newDate.hasSame(this.dateNow, 'day') === false
        ) {
          this.dateNow = newDate;
          this.onChangePeriodValue(periodValue);
        }
      });
  }

  onChangePeriodValue(value: any) {
    if (value == 'recently') {
      this.setRecentlyDates();
    } else if (value == 'today') {
      this.formGroup.patchValue({
        periodDateStart: transformLuxonDateToTuiDate(this.dateNow.startOf('day'), {}),
        periodDateEnd: transformLuxonDateToTuiDate(this.dateNow.endOf('day'), {}),
      });
    }
  }

  setRecentlyDates() {
    const { currentPeriod, recentlyPeriodType, recentlyPeriod } = this.formGroup.controls;

    const { startDate, endDate } = this.checkRecentlyPeriodType(
      recentlyPeriod.value,
      recentlyPeriodType.value,
      currentPeriod.value,
    );

    this.formGroup.patchValue({
      periodDateStart: transformLuxonDateToTuiDate(startDate, { withTime: true }),
      periodDateEnd: transformLuxonDateToTuiDate(endDate, { withTime: true }),
    });
  }

  checkRecentlyPeriodType(value: any, unit: any, withCurrentPeriod: any) {
    let startDate: any;
    let endDate: any;

    const minusDurationEnd = this.dateNow.minus(Duration.fromObject({ [unit]: 1 }));

    const minusDurationStart = (start: any) => {
      return start.minus(Duration.fromObject({ [unit]: value !== null ? value - 1 : null }));
    };

    if (unit === availableRecentlyDateTimePeriodTypes[0].value) {
      endDate = withCurrentPeriod ? this.dateNow.endOf('day') : minusDurationEnd.endOf('day');
      startDate = minusDurationStart(endDate).startOf('day');
    } else if (unit === availableRecentlyDateTimePeriodTypes[1].value) {
      endDate = withCurrentPeriod ? this.dateNow.endOf('day') : minusDurationEnd.endOf('week');
      startDate = minusDurationStart(endDate).startOf('week');
    } else if (unit === availableRecentlyDateTimePeriodTypes[2].value) {
      endDate = withCurrentPeriod ? this.dateNow.endOf('day') : minusDurationEnd.endOf('month');
      startDate = minusDurationStart(endDate).startOf('month');
    }

    return { startDate, endDate };
  }

  recentlyPeriodIncrement() {
    this.formGroup.controls.recentlyPeriod.setValue((this.formGroup.value.recentlyPeriod || 0) + 1);
  }

  recentlyPeriodDecrement() {
    const value = (this.formGroup.value.recentlyPeriod || 0) - 1;
    this.formGroup.controls.recentlyPeriod.setValue(value <= 0 ? 1 : value);
  }

  onFocusedChangePeriodDateStart($event: FocusEvent) {
    this.fillMissingTime(
      this.formGroup.controls.periodDateStart,
      $event.target as HTMLInputElement,
      new TuiTime(0, 0, 0, 0),
    );
  }

  onFocusedChangePeriodDateEnd($event: FocusEvent) {
    this.fillMissingTime(
      this.formGroup.controls.periodDateEnd,
      $event.target as HTMLInputElement,
      new TuiTime(23, 59, 59, 999),
    );
  }

  private fillMissingTime(
    control: FormControl<any>,
    input: HTMLInputElement,
    defaultTime: TuiTime,
  ) {
    const day = control.value?.[0];

    if (!day || input.value.includes(':')) return;

    control.setValue([day, defaultTime]);
  }
}
